/* eslint-disable no-underscore-dangle */
// triggers
const _triggerDefinition = name => ({ name, type: name.toUpperCase() })
// some pre-defined ones
const fetch = _triggerDefinition('fetch')
const login = _triggerDefinition('login')
const loginComplete = _triggerDefinition('loginComplete')
const logout = _triggerDefinition('logout')

// states
const _begin = 'BEGIN'
const _success = 'SUCCESS'
const _fail = 'FAIL'

// add workflow types. e.g. ('SOME_ACTION') => { BEGIN: SOME_ACTION_BEGIN, SUCCESS: SOME_ACTION_SUCCESS, FAIL: SOME_ACTION_FAIL }
const _states = base => [_begin, _success, _fail].reduce((acc, state) => {
	acc[state] = `${base}_${state}` // eslint-disable-line no-param-reassign, because this is exactly the right thing to be doing in a reducer
	return acc
}, {})

// add trigger type, and (maybe) trigger workflow types.
// e.g. ('SOME_ACTION', save) => { SAVE: SOME_ACTION_SAVE }
// e.g. ('SOME_ACTION', save, true) => { SAVE: SOME_ACTION_SAVE, save: { BEGIN: SOME_ACTION_SAVE_BEGIN, SUCCESS: SOME_ACTION_SAVE_SUCCESS, FAIL: SOME_ACTION_SAVE_FAIL } }
const _trigger = (base, def, workflow = false) => {
	const triggerBase = `${base}_${def.type}`
	return {
		[def.name]: {
			TRIGGER: triggerBase,
			...(workflow ? _states(triggerBase) : {})
		}
	}
}

// add workflow types and any specified trigger types (and their workflow types)
// e.g. ('SOME_ACTION') => { BEGIN: SOME_ACTION_BEGIN, SUCCESS: SOME_ACTION_SUCCESS, FAIL: SOME_ACTION_FAIL }
// e.g. ('SOME_ACTION', save) => { SAVE: SOME_ACTION_SAVE, save: { BEGIN: SOME_ACTION_SAVE_BEGIN, SUCCESS: SOME_ACTION_SAVE_SUCCESS, FAIL: SOME_ACTION_SAVE_FAIL } }
const _workflow = (base, ...triggers) => {
	if (triggers) {
		return triggers.reduce((acc, def) => {
			acc = { ...acc, ..._trigger(base, def, true) } // eslint-disable-line no-param-reassign, because this is exactly the right thing to be doing in a reducer
			return acc
		}, {})
	}
	return _states(base)
}

// normally, these are exported separately, then can be imported individually, or by "* as something"
const actionTypes = {
	FETCH_ERROR: 'FETCH_ERROR',
	FETCH_WARN: 'FETCH_WARN',

	VERIFYUSER_SET: 'VERIFYUSER_SET',
	VERIFYUSER_RESET: 'VERIFYUSER_RESET',
	VERIFYUSER_SUCCESS: 'VERIFYUSER_SUCCESS',
	VERIFYUSER_FAIL: 'VERIFYUSER_FAIL',

	userProfilePublic: _workflow('USERPROFILEPUBLIC', fetch),

	APP_SHOW_TOP_BAR: 'APP_SHOW_TOP_BAR',
	APP_MESSAGE: 'APP_MESSAGE',
	APP_REDIRECT: 'APP_REDIRECT',
	APP_REWRITE: 'APP_REWRITE',
	APP_CANNOT_REDIRECT_TO_SAME_URL: 'APP_CANNOT_REDIRECT_TO_SAME_URL',
	APP_BOTTOM_BAR_VALUE: 'APP_BOTTOM_BAR_VALUE',
	APP_BOTTOM_NAV_VALUE: 'APP_BOTTOM_NAV_VALUE',
	APP_HEADING: 'APP_HEADING',
	APP_BACK_BUTTON_FUNCTION: 'APP_BACK_BUTTON_FUNCTION',
	APP_SHOW_BOTTOM_BAR: 'APP_SHOW_BOTTOM_BAR',
	APP_SHOW_BOTTOM_NAV_BAR: 'APP_SHOW_BOTTOM_NAV_BAR',
	APP_BOTTOM_NAV_BAR_HANDLE_CLICK: 'APP_BOTTOM_NAV_BAR_HANDLE_CLICK',
	APP_BOTTOM_NAV_BAR_LABEL_NEXT: 'APP_BOTTOM_NAV_BAR_LABEL_NEXT',
	APP_NEXT_BUTTON_IS_LOADING: 'APP_NEXT_BUTTON_IS_LOADING',
	APP_DRAWER_OPEN: 'APP_DRAWER_OPEN',
	APP_PAGE_LOADED: 'APP_PAGE_LOADED',
	APP_SHOW_SNACKBAR: 'APP_SHOW_SNACKBAR',
	APP_BACKGROUND_IMAGE: 'APP_BACKGROUND_IMAGE',

	ITEM_FEATURE_VOTE: 'ITEM_FEATURE_VOTE',
	ITEM_FEATURE_VOTE_SAVE_BEGIN: 'ITEM_FEATURE_VOTE_SAVE_BEGIN',
	ITEM_FEATURE_VOTE_SAVE_SUCCESS: 'ITEM_FEATURE_VOTE_SAVE_SUCCESS',
	ITEM_FEATURE_VOTE_SAVE_FAIL: 'ITEM_FEATURE_VOTE_SAVE_FAIL',

	BRAND_FETCH_BEGIN: 'BRAND_FETCH_BEGIN',
	BRAND_FETCH_SUCCESS: 'BRAND_FETCH_SUCCESS',
	BRAND_FETCH_FAIL: 'BRAND_FETCH_FAIL',
	brandSuggestions: _workflow('BRAND_SUGGESTIONS', fetch),

	jerseyNameSuggestions: _workflow('JERSEYNAME_SUGGESTIONS', fetch),

	VOTE_FETCH_BEGIN: 'VOTE_FETCH_BEGIN',
	VOTE_FETCH_SUCCESS: 'VOTE_FETCH_SUCCESS',
	VOTE_FETCH_FAIL: 'VOTE_FETCH_FAIL',

	REPORT_SAVE_BEGIN: 'REPORT_SAVE_BEGIN',
	REPORT_SAVE_SUCCESS: 'REPORT_SAVE_SUCCESS',
	REPORT_SAVE_FAIL: 'REPORT_SAVE_FAIL',

	SEARCH_ITEM_BEGIN: 'SEARCH_ITEM_BEGIN',
	SEARCH_ITEM_SUCCESS: 'SEARCH_ITEM_SUCCESS',
	SEARCH_ITEM_FAIL: 'SEARCH_ITEM_FAIL',

	SEARCH_KIT_BEGIN: 'SEARCH_KIT_BEGIN',
	SEARCH_KIT_SUCCESS: 'SEARCH_KIT_SUCCESS',
	SEARCH_KIT_FAIL: 'SEARCH_KIT_FAIL',

	SEARCH_USER_BEGIN: 'SEARCH_USER_BEGIN',
	SEARCH_USER_SUCCESS: 'SEARCH_USER_SUCCESS',
	SEARCH_USER_FAIL: 'SEARCH_USER_FAIL',

	auth: {
		..._workflow('AUTH',
			logout,
			login),
		..._trigger('AUTH', _triggerDefinition('complete')),
		..._trigger('AUTH', _triggerDefinition('reset'))
	},
	authFacebookPopup: {
		..._workflow('AUTH_FACEBOOK_POPUP', login)
	},
	authFacebookRedirect: {
		..._workflow('AUTH_FACEBOOK_REDIRECT', login, loginComplete)
	},
	authEmail: {
		..._workflow('AUTH_EMAIL', login, loginComplete),
		..._trigger('AUTH_EMAIL', _triggerDefinition('sendEmail')),
		..._trigger('AUTH_EMAIL', _triggerDefinition('emailRequired')),
		..._trigger('AUTH_EMAIL', _triggerDefinition('loginAlreadyComplete'))
	},
	authInstagramPopup: {
		..._workflow('AUTH_INSTAGRAM_POPUP', login)
	},
	authInstagramRedirect: {
		..._workflow('AUTH_INSTAGRAM_REDIRECT', login, loginComplete)
	}

}

export default actionTypes



// WEBPACK FOOTER //
// ./src/redux/actionTypes/old_actionTypes.js